import { AFFIXES } from '../data/affixes.js';
import { getItem } from '../data/items.js';
import { affixPowerOf } from './affixes.js';
import { canEquip, equipmentBonus } from './inventory.js';
/**
 * « Better than what you wear »: the green arrow on a piece in the bag, at a
 * stall, at the auction house or on the ground. A display, never a rule the
 * server applies; it reads the same numbers as the tooltip's comparison.
 *
 * The three stats (ADR 0015) do not share a unit, so a piece is weighed in
 * its own power points (`piecePower`, ADR 0057): a stat counts as the power
 * an average affix of that stat would cost, and every other affix of the
 * fight counts, at the middle of its roll, as one affix's worth of the
 * piece's power. Two pieces of one slot then compare as one number, the
 * smith's +N and the instance's affixes included. Ceilings on the sum worn
 * are left out: the arrow says « a better piece », not « the best outfit ».
 */
/** The slots worn for their stats: never a companion, wings, a trail or a look. */
const STAT_SLOTS = [
    'weapon',
    'offhand',
    'helmet',
    'chest',
    'gloves',
    'boots',
];
function statSlotOf(template) {
    if (template.kind !== 'equipment' || template.cosmetic)
        return undefined;
    return STAT_SLOTS.find((slot) => slot === template.slot);
}
function midRoll(id) {
    const { min, max } = AFFIXES[id];
    return (min + max) / 2;
}
/** A piece's worth in power points: its stats at its level, then its fight affixes. */
export function pieceScore(piece) {
    const bonus = equipmentBonus([piece]);
    let score = bonus.dps / midRoll('dps') +
        bonus.health / midRoll('health') +
        bonus.defence / midRoll('defence');
    const power = affixPowerOf(piece.template);
    for (const affix of piece.affixes ?? []) {
        const definition = AFFIXES[affix.id];
        // dps, health and defence affixes are already in `equipmentBonus`.
        if (definition.scale === 'power')
            continue;
        score += (affix.value / midRoll(affix.id)) * power;
    }
    return score;
}
/** Whether the arrow may show on this template at all, whoever looks. */
export function isComparableGear(template) {
    return statSlotOf(template) !== undefined;
}
/**
 * Whether `candidate` would raise what this character wears in its slot: it
 * may wear it now (class, level) and it weighs more than the piece worn
 * there, or the slot is empty. Equal pieces get no arrow.
 */
export function isBetterThanWorn(candidate, wearer, equipped, templateOf = getItem) {
    const { template } = candidate;
    const slot = statSlotOf(template);
    if (slot === undefined || !canEquip(template, wearer).ok)
        return false;
    const instance = equipped?.[slot];
    const wornTemplate = instance === undefined ? undefined : templateOf(instance.templateId);
    const mine = pieceScore(candidate);
    if (instance === undefined || wornTemplate === undefined)
        return mine > 0;
    const theirs = pieceScore({
        template: wornTemplate,
        upgradeLevel: instance.upgradeLevel,
        affixes: instance.affixes,
    });
    // Scores are sums of fractions: a hair above is rounding, not a better piece.
    return mine - theirs > 1e-6;
}
//# sourceMappingURL=better-gear.js.map